import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * One line of the surplus calculation, read the way a planner reads a ledger:
 * an operator in a narrow gutter, the operand, and its amount or action at the
 * right edge. The composition editor, the saved/draft comparison and the hover
 * explanation all use this row, so the settings a user composes and the
 * numbers the diagnostic shows share one shape.
 */
export type LedgerOperator = '−' | '=';

export function LedgerRow({
  operator,
  label,
  detail,
  value,
  indent = false,
  emphasis = false,
  rule = false,
  children,
}: {
  operator?: LedgerOperator;
  label: ReactNode;
  /** One muted line under the label: a limitation, a date, a parameter. */
  detail?: ReactNode;
  /** The amount, or the row's control in the editor. */
  value?: ReactNode;
  /** A component beneath its operator line. */
  indent?: boolean;
  emphasis?: boolean;
  /** A rule above: the row is a result of the lines before it. */
  rule?: boolean;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-[1rem_minmax(0,1fr)_auto] items-start gap-x-2 py-1',
        rule && 'border-t',
        emphasis && 'font-medium',
      )}
    >
      <span
        aria-hidden="true"
        className="select-none text-right text-muted-foreground tabular-nums"
      >
        {operator}
      </span>
      <div className={cn('min-w-0', indent && 'pl-3')}>
        <div>{label}</div>
        {detail ? (
          <div className="text-xs font-normal text-muted-foreground">
            {detail}
          </div>
        ) : null}
        {children}
      </div>
      <div className="min-w-0 text-right tabular-nums">{value}</div>
    </div>
  );
}

/**
 * The operator line a group of components sits under: "Deductions · sum of
 * selected" with the group's amount, or in the editor its Add control.
 */
export function LedgerGroupRow({
  operator,
  title,
  operatorPhrase,
  value,
  rule = false,
}: {
  operator?: LedgerOperator;
  title: ReactNode;
  operatorPhrase: ReactNode;
  value?: ReactNode;
  rule?: boolean;
}) {
  return (
    <LedgerRow
      operator={operator}
      rule={rule}
      label={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">{title}</span>
          <span className="text-xs text-muted-foreground">
            {operatorPhrase}
          </span>
        </span>
      }
      value={value}
    />
  );
}
